import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { messageApi } from '@/api';
import { Button, Icon, LinkButton, Modal, Textarea, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';

const MAX_LENGTH = 4000;

/**
 * A company writing to a candidate it found in search, before any application.
 *
 * One thread per company and candidate: once written, the button opens that
 * thread instead of starting another. The lookup's key sits under
 * `['conversations']`, so a reply arriving over the socket refreshes it.
 * Hidden for members who may not send messages.
 */
export function MessageCandidateButton({ candidate, size = 'sm', variant = 'secondary' }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const allowed = can(PERMISSIONS.MESSAGE_SEND);
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sentId, setSentId] = useState(null);
  const firstName = candidate.full_name?.split(' ')[0] ?? 'the candidate';

  const existing = useQuery({
    queryKey: ['conversations', 'candidate', candidate.id],
    queryFn: () => messageApi.getCandidateConversation(candidate.id),
    enabled: allowed,
  });

  const send = useMutation({
    mutationFn: () => messageApi.messageCandidate(candidate.id, body.trim()),
    onSuccess: (result) => {
      setSentId(result.conversation.id);
      setBody('');
      toast.success(`Message sent to ${candidate.full_name}.`);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages-unread'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!allowed) return null;

  function close() {
    setOpen(false);
    setSentId(null);
  }

  // Kept on the modal while it is open: the refetch after sending finds the
  // new thread, and swapping the button then would unmount the dialog.
  if (existing.data && !open) {
    return (
      <LinkButton
        to={`${paths.company.messages}?c=${existing.data.id}`}
        variant={variant}
        size={size}
      >
        <Icon name="chat" size={15} />
        Open chat
      </LinkButton>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={existing.isPending}
        onClick={() => setOpen(true)}
      >
        <Icon name="chat" size={15} />
        Message
      </Button>

      <Modal
        open={open}
        title={sentId ? 'Message sent' : `Message ${candidate.full_name}`}
        description={
          sentId
            ? undefined
            : `${firstName} gets a notification and an email, and can reply in Messages.`
        }
        onClose={close}
        footer={
          sentId ? (
            <>
              <Button variant="secondary" onClick={close}>
                Close
              </Button>
              <LinkButton to={`${paths.company.messages}?c=${sentId}`}>
                Open conversation
              </LinkButton>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button
                isLoading={send.isPending}
                disabled={!body.trim()}
                onClick={() => send.mutate()}
              >
                <Icon name="send" size={16} />
                Send message
              </Button>
            </>
          )
        }
      >
        {sentId ? (
          <p className="text-ink-muted text-sm">
            It is in your messages. You will get a notification when {firstName} replies.
          </p>
        ) : (
          <Textarea
            label="Your message"
            rows={6}
            maxLength={MAX_LENGTH}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={`Hi ${firstName}, we came across your profile and think you'd be a great fit for an internship with us…`}
          />
        )}
      </Modal>
    </>
  );
}
